import * as React from 'react';

import { enumScannerStatus } from '@rematter/frontend-lib-api-client';
import { ScannerRecord } from '@rematter/modules/settings/types/sidecar';
import {
  Badge,
  Button,
  Col,
  Row,
  SearchField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCpu,
  LucidePlus,
  LucideRadioTower,
  LucideScanLine,
  LucideWrench,
} from 'lucide-react';

import { useScannerEventsSubscription } from '../../../_graphql/subscriptionScannerEvents';
import * as state from '../../../_state/scannersList.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { DemoScannerCreateDialog } from './DemoScannerCreateDialog';

import { useLibp2p } from '~/shared/providers/libp2p';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<ScannerRecord['status'], ThemeColor> = {
  [enumScannerStatus.Discovered]: 'warning',
};

const ScannerStatusCell: React.FC<{ scanner: ScannerRecord }> = ({
  scanner,
}) => {
  const t = useTranslations();
  const { connections } = useLibp2p();
  const presenceMembers = useHardwarePresenceMembers();
  const isOnline = presenceMembers.has(scanner.id);
  const conn = connections.get(scanner.sidecarId);

  return (
    <Row gap1 className="@ScannerStatusCell items-center">
      <Badge color={STATUS_COLORS[scanner.status]}>
        {t.hardwareView.scannersPanel.statusLabels[scanner.status]}
      </Badge>
      <Badge color={isOnline ? 'success' : 'neutral'}>
        {isOnline
          ? t.hardwareView.scannersPanel.presenceLabels.online
          : t.hardwareView.scannersPanel.presenceLabels.offline}
      </Badge>
      {conn?.connected && (
        <Badge color={conn.type === 'direct' ? 'primary' : 'neutral'}>
          {conn.type === 'direct'
            ? t.hardwareView.scannersPanel.connectionLabels.direct
            : t.hardwareView.scannersPanel.connectionLabels.relayed}
        </Badge>
      )}
    </Row>
  );
};

export const ScannersListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showDemoDialog, setShowDemoDialog] = React.useState(false);
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteScanners,
  );

  useScannerEventsSubscription(undefined, {
    onEvent: () => {
      refetch();
    },
  });

  const allScanners = data?.scanners ?? [];

  const scanners = React.useMemo(() => {
    if (!searchTerm.trim()) return allScanners;
    const term = searchTerm.toLowerCase();
    return allScanners.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.driverType.toLowerCase().includes(term) ||
        c.sidecar?.name.toLowerCase().includes(term),
    );
  }, [allScanners, searchTerm]);

  return (
    <Col gap4 className="@ScannersListView h-full p-4">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.hardwareView.scannersPanel.search.placeholder}
          aria-label={t.hardwareView.scannersPanel.search.ariaLabel}
          className="w-60 shrink-0"
        />
        <Button
          className="ml-auto"
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowDemoDialog(true)}
        >
          {t.hardwareView.demoCreateDialog.actions.addDemoScanner}
        </Button>
      </Row>
      <DemoScannerCreateDialog
        isOpen={showDemoDialog}
        onClose={() => setShowDemoDialog(false)}
        onSuccess={refetch}
      />
      <TinyTable<ScannerRecord>
        rowKey={(row) => row.id}
        isLoading={loadingState.isInitialLoading || isFetching}
        error={error}
        onRowClick={(row) =>
          setLocation(
            `/hardware/scanners/${encodeURIComponent(row.sidecarId)}/${encodeURIComponent(row.id)}`,
          )
        }
        columns={[
          {
            key: 'name',
            label: t.hardwareView.scannersPanel.columns.name,
            icon: LucideScanLine,
            width: 'auto',
            cell: (row) => (
              <div className="truncate font-medium">{row.name}</div>
            ),
          },
          {
            key: 'driverType',
            label: t.hardwareView.scannersPanel.columns.driverType,
            icon: LucideWrench,
            width: 180,
            cell: (row) => <div className="truncate">{row.driverType}</div>,
          },
          {
            key: 'status',
            label: t.hardwareView.scannersPanel.columns.status,
            icon: LucideRadioTower,
            width: 'max-content',
            cell: (row) => <ScannerStatusCell scanner={row} />,
          },
          {
            key: 'sidecar',
            label: t.hardwareView.scannersPanel.columns.sidecar,
            icon: LucideCpu,
            width: 220,
            cell: (row) => (
              <div className="truncate">{row.sidecar?.name ?? '\u2013'}</div>
            ),
          },
        ]}
        data={scanners}
      />
    </Col>
  );
};
